/**
 * Filter matchmaking units for game-type priority passes so cross-gender
 * partnerships are not broken when solo players of the required gender exist.
 */

import { UNIT_TYPES } from './unitBuilder.js';
import { isMensDoublesEligible } from './competitiveGender.js';

function playerGender(playerId, playersById) {
  return (playersById?.[playerId]?.gender || '').toLowerCase();
}

function isMale(playerId, playersById) {
  return playerGender(playerId, playersById) === 'male';
}

function isFemale(playerId, playersById) {
  return playerGender(playerId, playersById) === 'female';
}

/**
 * Whether a player is effectively male for the mens hint.
 * Physical males and mens-doubles-eligible females both qualify.
 */
function isEffectiveMaleForHint(playerId, playersById, playerWaitMetrics) {
  if (isMale(playerId, playersById)) return true;
  return isFemale(playerId, playersById) && isMensDoublesEligible(playerId, playerWaitMetrics);
}

function isCrossGenderPartnershipUnit(unit, playersById) {
  if (unit?.type !== UNIT_TYPES.PARTNERSHIP || !unit.players?.length) return false;
  const [p1, p2] = unit.players;
  return isMale(p1, playersById) !== isMale(p2, playersById);
}

function isSameGenderPartnershipUnit(unit, playersById) {
  if (unit?.type !== UNIT_TYPES.PARTNERSHIP || unit.players?.length !== 2) return false;
  const [p1, p2] = unit.players;
  return isMale(p1, playersById) === isMale(p2, playersById);
}

/**
 * Whether a unit can appear in the partnership-safe pool for a game-type hint.
 *
 * mens   — solo effective-males (physical males + eligible females) and effective-male partnerships
 * womens — solo non-eligible females and non-eligible-female partnerships
 * mixed  — solo players and cross-gender partnerships (not same-gender pairs)
 *
 * @param {Object} unit
 * @param {Object} playersById
 * @param {string} hint
 * @param {Object} [playerWaitMetrics={}]
 */
export function unitEligibleForGameTypeHint(unit, playersById, hint, playerWaitMetrics = {}) {
  if (!hint || !unit) return true;
  if (unit.type === UNIT_TYPES.SCHEDULED) return true;

  if (unit.type === UNIT_TYPES.SINGLE) {
    const playerId = unit.players?.[0];
    if (!playerId) return false;
    if (hint === 'mens') return isEffectiveMaleForHint(playerId, playersById, playerWaitMetrics);
    if (hint === 'womens') {
      // Eligible females are redirected to mens pool; exclude from womens
      return isFemale(playerId, playersById) && !isMensDoublesEligible(playerId, playerWaitMetrics);
    }
    return true;
  }

  if (unit.type === UNIT_TYPES.PARTNERSHIP) {
    if (hint === 'mens') {
      // Both players must be effectively male (physical or eligible-female)
      return unit.players.every((id) => isEffectiveMaleForHint(id, playersById, playerWaitMetrics));
    }
    if (hint === 'womens') {
      // Both must be non-eligible females
      return unit.players.every(
        (id) => isFemale(id, playersById) && !isMensDoublesEligible(id, playerWaitMetrics)
      );
    }
    if (hint === 'mixed') {
      return isCrossGenderPartnershipUnit(unit, playersById);
    }
  }

  return true;
}

export function filterUnitsForGameTypeHint(units, playersById, hint, playerWaitMetrics = {}) {
  if (!hint || !Array.isArray(units)) return units || [];
  return units.filter((unit) => unitEligibleForGameTypeHint(unit, playersById, hint, playerWaitMetrics));
}

export {
  isCrossGenderPartnershipUnit,
  isSameGenderPartnershipUnit,
};
